import { inject, Injectable } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { UserAddress, UserRoles } from 'app/core/models/user.model';
import { UserCoupon } from '../dtos/user-coupon.dto';
import { CustomValidators } from 'app/core/validators/custom-validators';
import { PhysicianProcedure } from '../../../core/dtos/physician.dto';

@Injectable({
  providedIn: 'root',
})
export class CouponFormsService {
  private fb = inject(FormBuilder);
  private _formInfo = this.fb.nonNullable.group({
    beneficiaryHasProcedures: [false],
  });

  private _formUser = this.fb.nonNullable.group({
    id: [''],
    name: ['', [Validators.required]],
    cpf: [
      '',
      [Validators.required, Validators.minLength(11), CustomValidators.cpf()],
    ],
    birthDate: ['', [Validators.required]],
    email: ['', [Validators.required]],
    phone: [''],
    pushToken: [''],
    roles: [[] as UserRoles[]],
    s4eUserId: ['', [Validators.required]],
    hasAppointmentCandidate: [false],
    couponProcedures: [[] as UserCoupon[]],
  });

  private _formAddress = this.fb.nonNullable.group({
    id: [''],
    title: [''],
    zipCode: [''],
    state: [''],
    city: [''],
    neighborhood: [''],
    street: ['', [Validators.required]],
    number: [''],
    complement: [''],
    reference: [''],
    latitude: [''],
    longitude: [''],
    selectedAddress: [undefined as UserAddress | undefined],
  });

  private _formPhysician = new FormGroup({
    physicianId: new FormControl<number | null>(null),
    physicianName: new FormControl('', [Validators.required]),
    physicianCpf: new FormControl(''),
    physicianBirthDate: new FormControl(''),
    physicianCro: new FormControl<number | null>(null),
    physicianPhone: new FormControl(''),
    physicianEmail: new FormControl(''),
    physicianBranchCode: new FormControl<number | null>(null),
    physicianBranchName: new FormControl(''),
    addressStreet: new FormControl(''),
    addressNumber: new FormControl<number | null>(null),
    addressNeighborhood: new FormControl(''),
    addressCity: new FormControl(''),
    addressState: new FormControl(''),
    addressZipCode: new FormControl(''),
    longitude: new FormControl<number | null>(null),
    latitude: new FormControl<number | null>(null),
    distanceInKilometers: new FormControl<number | null>(null),
    score: new FormControl<number | null>(null),
    accredited: new FormControl<boolean | null>(null),
    hasCandidates: new FormControl(false),
  });

  private _formProcedure = new FormGroup({
    physicianProceduresSearch: new FormControl(''),
    selectedProcedures: new FormControl<PhysicianProcedure[]>(
      [],
      [CustomValidators.required()],
    ),
  });

  private _formAppointment = new FormGroup({
    appointmentDateTime: new FormControl<{
      date: Date | null;
      time: Date | null;
    }>({ date: null, time: null }, [
      Validators.required,
      CustomValidators.dateTimeRequired('Selecione uma data e horário válidos'),
    ]),
  });

  private _formUserAddresses = new FormArray<FormGroup>([]);

  public get formUser() {
    return this._formUser;
  }

  public get formAddress() {
    return this._formAddress;
  }

  public get formPhysician() {
    return this._formPhysician;
  }

  public get formProcedures() {
    return this._formProcedure;
  }

  public get formAppointment() {
    return this._formAppointment;
  }

  public get formUserAddresses() {
    return this._formUserAddresses;
  }

  public get formInfo() {
    return this._formInfo;
  }

  public createAddressFormGroup(address?: UserAddress) {
    return new FormGroup({
      id: new FormControl(address?.id || ''),
      title: new FormControl(address?.title || '', Validators.required),
      zipCode: new FormControl(address?.zipCode || '', [
        Validators.required,
        Validators.pattern(/^\d{8}$/),
      ]),
      state: new FormControl(address?.state || '', Validators.required),
      city: new FormControl(address?.city || '', Validators.required),
      neighborhood: new FormControl(address?.neighborhood || ''),
      street: new FormControl(address?.street || '', Validators.required),
      number: new FormControl(address?.number || '', Validators.required),
      complement: new FormControl(address?.complement || ''),
      reference: new FormControl(address?.reference || ''),
      latitude: new FormControl(address?.latitude || 0),
      longitude: new FormControl(address?.longitude || 0),
    });
  }

  public setUserAddresses(addresses: UserAddress[]): void {
    this._formUserAddresses.clear();
    addresses.forEach(address => {
      this._formUserAddresses.push(this.createAddressFormGroup(address));
    });
  }

  public addUserAddress(address: UserAddress): void {
    this._formUserAddresses.push(this.createAddressFormGroup(address));
  }

  public updateUserAddress(index: number, address: UserAddress): void {
    if (index >= 0 && index < this._formUserAddresses.length) {
      const form = this._formUserAddresses.at(index) as FormGroup;
      form.patchValue(address);
    }
  }

  public removeUserAddress(index: number): void {
    if (index >= 0 && index < this._formUserAddresses.length) {
      this._formUserAddresses.removeAt(index);
    }
  }

  public findUserAddressById(
    id: string,
  ): { form: FormGroup; index: number } | undefined {
    for (let i = 0; i < this._formUserAddresses.length; i++) {
      const form = this._formUserAddresses.at(i) as FormGroup;
      if (form.value.id === id) {
        return { form, index: i };
      }
    }
    return undefined;
  }

  public getUserAddressesAsArray(): UserAddress[] {
    return this._formUserAddresses.controls.map(control => control.value);
  }

  public resetForms() {
    this._formUser.reset();
    this._formAddress.reset();
    this._formPhysician.reset();
    this._formProcedure.reset();
    this._formAppointment.reset();
    this._formUserAddresses.clear();
    this._formInfo.reset();
  }
}
